import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { QPilotCoupon } from "../../../core/models/qpilot/coupon.interface";
import { CouponFilter, CouponListResponse } from "../interfaces";
import { QPilotAccessToken } from "../../../core/services/qpilot.service";

/**
 * Import result from CSV upload
 */
export interface CouponImportResult {
  Message?: string;
  ErrorsList?: Array<{
    Row: number;
    ErrorMessage: string;
  }>;
}

/**
 * Product or ProductGroup model for coupon targeting autocomplete
 * Based on old QMC QpilotProductGroupModel
 */
export interface ProductGroupSearchResult {
  productAndGroupId: number;
  externalId: string;
  productAndGroupName: string;
  price: number;
  salePrice: number;
  type: "Prod" | "Group";
}

/**
 * Offer item from retention workflow
 */
export interface OfferItem {
  itemId: string;
  itemType: string;
}

/**
 * QPilot Offer for retention workflow check
 * Note: status can be number (0=Accepted, 1=Rejected, 2=Pending) or string
 * Old QMC uses QPilotOfferStatusTypes enum: Accepted=0, Rejected=1, Pending=2
 */
export interface QPilotOffer {
  id: string;
  status: string | number;
  items: OfferItem[];
}

/**
 * Response from getSiteOffers API
 */
export interface SiteOffersResponse {
  offers: QPilotOffer[];
}

/**
 * Coupons Service
 * Handles all coupon-related API calls
 * Based on old QMC qpilot.service.ts coupon methods
 */
@Injectable({ providedIn: "root" })
export class CouponsService {
  private readonly http = inject(HttpClient);
  // Base API URL matching old QMC pattern: environment.qpilotApiUrl + "/api"
  private readonly apiUrl = `${environment.qpilotApiUrl}/api`;

  /**
   * Get paginated list of coupons for a site
   * Old QMC: GET /Sites/{siteId}/Coupons?page={page}&pageSize={pageSize}&orderBy={orderBy}&order={order}&search={search}
   * See qpilot.service.ts:getCouponsForSite()
   * @param siteId - Site ID
   * @param filter - Filter parameters
   * @param accessToken - Optional access token for embedded mode
   */
  getCoupons(
    siteId: number,
    filter: CouponFilter,
    accessToken?: QPilotAccessToken,
  ): Observable<CouponListResponse> {
    let params = new HttpParams()
      .set("page", filter.page.toString())
      .set("pageSize", filter.pageSize.toString())
      .set("orderBy", filter.orderBy)
      .set("order", filter.order);

    if (filter.search?.trim()) {
      params = params.set("search", filter.search.trim());
    }

    if (filter.discountType) {
      params = params.set("discountType", filter.discountType);
    }

    const headers = this.createHeaders(accessToken);
    return this.http.get<CouponListResponse>(
      `${this.apiUrl}/Sites/${siteId}/Coupons`,
      { params, headers },
    );
  }

  /**
   * Get single coupon by ID
   * Old QMC: GET /Coupons/{id}
   * See qpilot.service.ts:getCoupon()
   * @param couponId - Coupon ID
   * @param accessToken - Optional access token for embedded mode
   */
  getCoupon(
    couponId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<QPilotCoupon> {
    const headers = this.createHeaders(accessToken);
    return this.http.get<QPilotCoupon>(`${this.apiUrl}/Coupons/${couponId}`, {
      headers,
    });
  }

  /**
   * Create new coupon
   * Old QMC: POST /Coupons
   * See qpilot.service.ts:createCoupon()
   * @param coupon - Coupon data
   * @param accessToken - Optional access token for embedded mode
   */
  createCoupon(
    coupon: Partial<QPilotCoupon>,
    accessToken?: QPilotAccessToken,
  ): Observable<QPilotCoupon> {
    const headers = this.createHeaders(accessToken);
    return this.http.post<QPilotCoupon>(`${this.apiUrl}/Coupons`, coupon, {
      headers,
    });
  }

  /**
   * Update existing coupon
   * Old QMC: PUT /Coupons/{id}
   * See qpilot.service.ts:updateCoupon()
   * @param couponId - Coupon ID
   * @param coupon - Coupon data
   * @param accessToken - Optional access token for embedded mode
   */
  updateCoupon(
    couponId: number,
    coupon: Partial<QPilotCoupon>,
    accessToken?: QPilotAccessToken,
  ): Observable<QPilotCoupon> {
    const headers = this.createHeaders(accessToken);
    return this.http.put<QPilotCoupon>(
      `${this.apiUrl}/Coupons/${couponId}`,
      coupon,
      { headers },
    );
  }

  /**
   * Delete coupon
   * Old QMC: DELETE /Coupons/{id}
   * See qpilot.service.ts:deleteCoupon()
   * @param couponId - Coupon ID
   * @param accessToken - Optional access token for embedded mode
   */
  deleteCoupon(
    couponId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<void> {
    const headers = this.createHeaders(accessToken);
    return this.http.delete<void>(`${this.apiUrl}/Coupons/${couponId}`, {
      headers,
    });
  }

  /**
   * Validate coupon code uniqueness
   * Old QMC: GET /Sites/{siteId}/Coupons/CheckSingle?couponCode={code}&couponId={couponId}
   * See qpilot.service.ts:validateCoupon()
   *
   * @param siteId - Site ID
   * @param code - Coupon code to validate
   * @param couponId - Optional coupon ID (for edit mode)
   * @param accessToken - Optional access token for embedded mode
   * @returns null if valid, error message string if invalid
   */
  validateCouponCode(
    siteId: number,
    code: string,
    couponId?: number,
    accessToken?: QPilotAccessToken,
  ): Observable<string | null> {
    let params = new HttpParams().set("couponCode", code);

    if (couponId) {
      params = params.set("couponId", couponId.toString());
    }

    const headers = this.createHeaders(accessToken);
    return this.http.get<string | null>(
      `${this.apiUrl}/Sites/${siteId}/Coupons/CheckSingle`,
      { params, headers },
    );
  }

  /**
   * Export coupons to CSV
   * Old QMC: GET /Sites/{siteId}/Coupons/Export
   * See qpilot.service.ts:exportCouponsForSite()
   *
   * Note: API returns CSV as a JSON-encoded string, so we use http.get<string>
   * to let Angular parse the JSON and return the actual CSV content.
   *
   * @param siteId - Site ID
   * @param accessToken - Optional access token for embedded mode
   * @returns CSV string content
   */
  exportCoupons(
    siteId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<string> {
    const headers = this.createHeaders(accessToken);
    return this.http.get<string>(
      `${this.apiUrl}/Sites/${siteId}/Coupons/Export`,
      { headers },
    );
  }

  /**
   * Import coupons from CSV file
   * Old QMC: POST /Sites/{siteId}/Coupons/Import
   * See qpilot.service.ts:uploadFile() with "/sites/{siteId}/Coupons/Import"
   *
   * @param siteId - Site ID
   * @param file - CSV file to import
   * @param accessToken - Optional access token for embedded mode
   * @returns Import result with optional message or errors list
   */
  importCoupons(
    siteId: number,
    file: File,
    accessToken?: QPilotAccessToken,
  ): Observable<CouponImportResult> {
    const formData = new FormData();
    formData.append("file", file);

    const headers = this.createHeaders(accessToken);
    return this.http.post<CouponImportResult>(
      `${this.apiUrl}/Sites/${siteId}/Coupons/Import`,
      formData,
      { headers },
    );
  }

  /**
   * Search products and product groups for coupon targeting
   * Old QMC: GET /Sites/{siteId}/Products/ProductsAndProductGroup?page={page}&search={search}
   * See qpilot.service.ts:getProductsByGroupForSite()
   * Note: This endpoint does NOT use /api/ prefix - uses qpilotApiUrl directly
   * Note: API returns PascalCase, we normalize to camelCase for consistent usage
   * @param siteId - Site ID
   * @param search - Search query
   * @param page - Page number
   * @param accessToken - Optional access token for embedded mode
   */
  searchProductsAndGroups(
    siteId: number,
    search: string = "",
    page: number = 1,
    accessToken?: QPilotAccessToken,
  ): Observable<ProductGroupSearchResult[]> {
    let params = new HttpParams().set("page", page.toString());

    if (search?.trim()) {
      params = params.set("search", search.trim());
    }

    const headers = this.createHeaders(accessToken);
    // Use qpilotApiUrl directly without /api/ prefix (matches old QMC)
    // Map PascalCase API response to camelCase for consistent property access
    return this.http
      .get<
        Record<string, unknown>[]
      >(`${environment.qpilotApiUrl}/Sites/${siteId}/Products/ProductsAndProductGroup`, { params, headers })
      .pipe(
        map((results) =>
          results.map((item) => ({
            productAndGroupId:
              (item["ProductAndGroupId"] as number) ||
              (item["productAndGroupId"] as number) ||
              0,
            externalId:
              (item["ExternalId"] as string) ||
              (item["externalId"] as string) ||
              "",
            productAndGroupName:
              (item["ProductAndGroupName"] as string) ||
              (item["productAndGroupName"] as string) ||
              "",
            price: (item["Price"] as number) || (item["price"] as number) || 0,
            salePrice:
              (item["SalePrice"] as number) ||
              (item["salePrice"] as number) ||
              0,
            type: ((item["Type"] as string) ||
              (item["type"] as string) ||
              "Prod") as "Prod" | "Group",
          })),
        ),
      );
  }

  /**
   * Get site offers (retention workflows) to check if coupon is in use
   * Old QMC: GET /v1/offers/{siteId}
   * See qpilot.service.ts:getSiteOffers()
   * Note: This uses qpilotNonCoreApiUrl (not qpilotApiUrl!)
   * Old QMC uses: this.qpilotNonCoreProcessesUrl = environment.qpilotNonCoreApiUrl
   * @param siteId - Site ID
   * @param accessToken - Optional access token for embedded mode
   */
  getSiteOffers(
    siteId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<SiteOffersResponse> {
    const headers = this.createHeaders(accessToken);
    // The offers API uses the non-core processes URL
    // Matches old QMC: `${this.qpilotNonCoreProcessesUrl}/v1/offers/${siteId}`
    return this.http.get<SiteOffersResponse>(
      `${environment.qpilotNonCoreApiUrl}/v1/offers/${siteId}`,
      { headers },
    );
  }

  /**
   * Build HTTP params from filter object
   * Helper method for constructing query parameters
   */
  private buildFilterParams(filter: CouponFilter): HttpParams {
    let params = new HttpParams()
      .set("page", filter.page.toString())
      .set("pageSize", filter.pageSize.toString())
      .set("orderBy", filter.orderBy)
      .set("order", filter.order);

    if (filter.search?.trim()) {
      params = params.set("search", filter.search.trim());
    }

    if (filter.discountType) {
      params = params.set("discountType", filter.discountType);
    }

    return params;
  }

  /**
   * Creates HTTP headers for authenticated requests
   * Follows the same pattern as QPilotService.createHeaders()
   * @param accessToken - Optional access token for embedded mode
   * @returns HTTP headers with Authorization if token provided
   */
  private createHeaders(accessToken?: QPilotAccessToken): HttpHeaders {
    let headers = new HttpHeaders();

    if (accessToken) {
      if (accessToken.TokenBearerAuth) {
        headers = headers.set(
          "Authorization",
          `Bearer ${accessToken.TokenBearerAuth}`,
        );
      } else if (accessToken.TokenAuth) {
        headers = headers.set(
          "Authorization",
          `Basic ${accessToken.TokenAuth}`,
        );
      }
    }

    return headers;
  }
}
